import { addRecentItem } from '@/lib/store/shared/recently-viewed'
import { ContentType, ContentTypeToKey } from '@/lib/types/content'
import { recentlyViewedItemSchema } from '@/lib/validation/store/shared'
import { Middleware } from '@reduxjs/toolkit'

interface SetItemAction {
  type: string
  payload: {
    id: string
    type: ContentType
    slug?: string
    path?: string
    [key: string]: any
  }
}

// Type guard for set item actions
const isSetItemAction = (action: unknown): action is SetItemAction => {
  if (
    typeof action === 'object' &&
    action !== null &&
    'type' in action &&
    'payload' in action &&
    typeof (action as any).payload === 'object' &&
    (action as any).payload !== null &&
    'id' in (action as any).payload &&
    'type' in (action as any).payload
  ) {
    const type = (action as any).type as string
    return type.startsWith('products/setSelected') ||
      type.startsWith('articles/setSelected') ||
      type.startsWith('services/setSelected') ||
      type.startsWith('projects/setSelected') ||
      type.startsWith('locations/setSelected')
  }
  return false
}

const getIconForType = (type: ContentType): string => {
  switch (type) {
    case ContentType.ARTICLE:
      return 'content-article'
    case ContentType.PRODUCT:
      return 'content-product'
    case ContentType.PROJECT:
      return 'content-project'
    case ContentType.SERVICE:
      return 'content-service'
    case ContentType.LOCATION:
      return 'content-location'
    default:
      return 'content-default'
  }
}

const getColorForType = (type: ContentType): string => {
  switch (type) {
    case ContentType.ARTICLE:
      return 'text-primary-500'
    case ContentType.PRODUCT:
      return 'text-success-500'
    case ContentType.PROJECT:
      return 'text-warning-500'
    case ContentType.SERVICE:
      return 'text-secondary-500'
    case ContentType.LOCATION:
      return 'text-info-500'
    default:
      return 'text-foreground-500'
  }
}

export const syncRecentlyViewedMiddleware: Middleware = (store) => (next) => (action) => {
  const result = next(action)

  if (isSetItemAction(action)) {
    const { payload } = action
    const state = store.getState()

    const sliceKey = ContentTypeToKey[payload.type]

    if (!sliceKey) {
      return result
    }

    const contentTypeToSelectedField: Record<ContentType, string> = {
      [ContentType.PRODUCT]: 'selectedProduct',
      [ContentType.ARTICLE]: 'selectedArticle',
      [ContentType.SERVICE]: 'selectedService',
      [ContentType.PROJECT]: 'selectedProject',
      [ContentType.LOCATION]: 'selectedLocation',
      [ContentType.NONE]: 'selectedItem',
    }

    const selectedFieldName = contentTypeToSelectedField[payload.type] || 'selectedItem'
    const selectedItem = state[sliceKey][selectedFieldName]

    if (!selectedItem) {
      return result
    }

    if (selectedItem.id === payload.id) {
      try {
        const recentItem = {
          ...selectedItem,
          id: payload.id,
          type: payload.type,
          slug: selectedItem.slug || payload.slug,
          path: selectedItem.path || `/${payload.type.toLowerCase()}s/${selectedItem.slug || payload.id}`,
          href: `/${payload.type.toLowerCase()}s/${selectedItem.slug || payload.id}`,
          icon: getIconForType(payload.type),
          color: getColorForType(payload.type),
          updatedAt: new Date().toISOString()
        }

        // Validate the recent item before dispatching
        const validationResult = recentlyViewedItemSchema.safeParse(recentItem)
        if (validationResult.success) {
          store.dispatch(addRecentItem(validationResult.data))
        } else {
          console.error('Invalid recently viewed item:', validationResult.error)
        }
      } catch (error) {
        console.error('Error adding recent item:', error)
      }
    }
  }

  return result
}
